import {
  FileData,
  FileManagerModel,
  ColumnModel,
  DetailsViewSettingsModel,
  FileOpenEventArgs,
  ToolbarItem,
} from "@syncfusion/ej2-react-filemanager";
import { DefaultHtmlAttributes } from "@syncfusion/ej2-react-base";
import { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";

export interface FileExplorerGridColumn extends ColumnModel {
  visibleForFiles?: boolean;
  visibleForFolders?: boolean;
}

export const FileExplorerGridColumns = {
  name: {
    field: "name",
    headerText: "Name",
    width: 300,
    customAttributes: { class: "e-fe-grid-name" },
    template: '<span class="e-fe-text">${name}</span>',
  },
  size: {
    field: "size",
    headerText: "Size",
    minWidth: 50,
    width: "110",
    template: '<span class="e-fe-size">${size}</span>',
  },
  dateModified: {
    field: "_fm_modified",
    headerText: "DateModified",
    minWidth: 50,
    width: "190",
  },
};

export interface FlatFileData<T> extends FileData {
  color?: FlatFileColor;
  isPinnedAt?: string;
  canBePinned?: boolean;
  visible?: boolean;
  data?: T;
  badge?: string;
  detailsViewSettings?: DetailsViewSettingsModel;
  _pinnedData?: {
    originalParentId?: string | number;
    originalParentFilterPath?: string;
  };
}

export type FlatFileManagerSettings<T> = Omit<
  FileManagerModel | DefaultHtmlAttributes,
  "fileSystemData" | "view" | "cssClass"
> & {
  allowUpload?: boolean;
  defaultOpenFolderId?: () => string;
  showAddressBar?: boolean;
  onPinClick?: (args: FlatFileData<T>) => void;
  // Have permission to pin folders
  allowPinnedFolders?: boolean;
  maxPinnedFolders?: number;
  onNewFolderClick?: () => void;
  created?: (args: Object) => string;
  fileOpen?: (args: FileOpenEventArgs) => void;
  fileSelect?: (args: any) => void;
  onReset?: (args: Object) => string;
};

export type FlatFileColor =
  | "neutral-gray"
  | "silver"
  | "sky-blue"
  | "sunset-red"
  | "golden-yellow"
  | "midnight-navy"
  | "forest-green"
  | "desert-sand"
  | "peach-blush"
  | "cool-mint"
  | "deep-purple"
  | "steel-gray"
  | "bright-coral"
  | "warm-mocha"
  | "citrus-orange"
  | "lemon-zest"
  | "lavender-fog"
  | "aqua-wave"
  | "charcoal"
  | "cream"
  | "lime-punch"
  | "dusty-rose"
  | "ocean-teal"
  | "storm-blue"
  | "platinum";

export const FlatFileColors: Record<string, string> = {
  "neutral-gray": "#C0C0C0",
  silver: "#C0C0C0",
  "sky-blue": "#4D85EB",
  "sunset-red": "#E96077",
  "golden-yellow": "#F9C74F",
  "midnight-navy": "#1E2A38",
  "forest-green": "#63A480",
  "desert-sand": "#EDC9AF",
  "peach-blush": "#FFCBA4",
  "cool-mint": "#A3E4D7",
  "deep-purple": "#6C3483",
  "steel-gray": "#A9A9A9",
  "bright-coral": "#FF6F61",
  "warm-mocha": "#A9746E",
  "citrus-orange": "#FFA500",
  "lemon-zest": "#FFD166",
  "lavender-fog": "#D8BFD8",
  "aqua-wave": "#00CED1",
  charcoal: "#444444",
  cream: "#FFFDD0",
  "lime-punch": "#D4ED00",
  "dusty-rose": "#C08081",
  "ocean-teal": "#008080",
  "storm-blue": "#5A7D9A",
  platinum: "#E5E4E2",
};

export interface FileExplorerToolbarItem<T = any> extends Partial<ToolbarItem> {
  id: string; // Required - needed for DOM element lookup
  name: string; // Required - needed for Syncfusion toolbar
  template: string; // Required - needed for custom content rendering
  actionType: string; // Required - needed for DOM element lookup

  // Add dynamic behavior we need
  buttonSettings?: {
    isDisabled?: (data?: T) => boolean;
    onClick: (data?: T) => void;
    minimal?: boolean;
    iconProps?: FontAwesomeIconProps;
    iconPosition?: string;
  };
  searchSettings?: {
    onSearch?: (value: string, data?: T) => void;
    placeholder?: string;
    isDisabled?: (data?: T) => boolean;
  };
  textSettings?: {
    getText: (data?: T) => string;
  };
}

export const FlatFileExplorerToolbarItemTypes = {
  Search: "Search",
  NewFolder: "NewFolder",
  CustomButton: "CustomButton",
  CustomText: "CustomText",
};

export type FlatFileExplorerToolbarItemType =
  typeof FlatFileExplorerToolbarItemTypes[keyof typeof FlatFileExplorerToolbarItemTypes];
